import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { api, type SalesProduct } from './api';
import { KAYITLI_URUN as T } from './metin/kayitli-urun';
import { kalemTuru } from './metin/ortak';
import { paraAltBirime } from './quote-pricing';
import './saved-product-picker.css';

const ITEM_TYPES = ['HOTEL', 'TRANSFER', 'TOUR', 'ACTIVITY', 'VISA', 'HELICOPTER', 'YACHT', 'TICKET', 'ESIM', 'OTHER'] as const;
const PAGE_SIZE = 20;
const CURRENCIES = ['AED', 'USD', 'EUR', 'TRY'] as const;
const clean = (value: string) => value.trim().replace(/\s+/g, ' ');
const altBirimText = (minor: number) => (minor / 100).toFixed(2).replace('.', ',');
const usdLine = (product: SalesProduct): string | null => {
  if (product.currencyCode === 'USD' || product.priceUsdMinor === null || product.priceUsdMinor === undefined || product.fxRateMicros === null || product.fxRateMicros === undefined) return null;
  const usd = amountText(product.priceUsdMinor, 'USD');
  if (product.currencyCode === 'AED') return T.usdEquivalent(usd, T.usdRateAed('3,6725'));
  const date = product.fxRateDate ? new Intl.DateTimeFormat('tr-TR', { day: '2-digit', month: '2-digit', timeZone: 'UTC' }).format(new Date(product.fxRateDate)) : '';
  return T.usdEquivalent(usd, T.usdRateEur(new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 }).format(product.fxRateMicros / 1_000_000), date));
};
const amountText = (value: number, currency: string) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency }).format(value / 100);

/** These records belong to CRM. Saving never publishes a product to the website. */
export function SavedProductPicker({ currency, onAdd }: { currency: string; onAdd: (product: SalesProduct) => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'list' | 'new' | 'edit'>('list');
  const [editing, setEditing] = useState<SalesProduct | null>(null);
  const [editCurrency, setEditCurrency] = useState('AED');
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const [products, setProducts] = useState<SalesProduct[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [retry, setRetry] = useState(0);
  const [listError, setListError] = useState('');
  const [name, setName] = useState('');
  const [itemType, setItemType] = useState<string>('VISA');
  const [description, setDescription] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [price, setPrice] = useState('');
  const [tax, setTax] = useState('0');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [notice, setNotice] = useState('');
  const savingRef = useRef(false);

  useEffect(() => {
    if (open) dialog.current?.showModal();
    else dialog.current?.close();
  }, [open]);

  useEffect(() => {
    if (!open || mode !== 'list') return;
    let active = true;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    setLoading(true); setListError(''); setProducts([]);
    const debounce = setTimeout(() => {
      timeout = setTimeout(() => {
        if (!active) return;
        active = false; setLoading(false); setListError(T.listTimeout);
      }, 15_000);
      void api.salesProducts({ q: query.trim() || undefined, offset, limit: PAGE_SIZE })
        .then(result => {
          if (!active) return;
          setProducts(result.items); setTotal(result.total);
        })
        .catch(error => {
          if (active) setListError(error instanceof Error ? error.message : T.listFailed);
        })
        .finally(() => {
          if (timeout) clearTimeout(timeout);
          if (active) setLoading(false);
        });
    }, 250);
    return () => { active = false; clearTimeout(debounce); if (timeout) clearTimeout(timeout); };
  }, [open, mode, query, offset, retry]);

  const launch = (nextMode: 'list' | 'new') => {
    setMode(nextMode); setOpen(true); setSaveError(''); setNotice(''); setOffset(0);
  };
  const add = (product: SalesProduct) => {
    onAdd(product);
    setNotice(product.currencyCode !== currency && product.id !== 'ozel-paket'
      ? T.mismatchAdded(product.name, product.currencyCode, amountText(product.unitPriceMinor, product.currencyCode), currency)
      : T.added(product.name));
    setOpen(false);
  };
  const startEdit = (product: SalesProduct) => {
    setEditing(product); setEditCurrency(product.currencyCode); setSaveError('');
    setName(product.name); setItemType(product.itemType); setDescription(product.description ?? ''); setSupplierName(product.supplierName ?? '');
    setPrice(altBirimText(product.unitPriceMinor)); setTax(altBirimText(product.taxRateBps)); setMode('edit');
  };
  const saveEdit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (savingRef.current || !editing) return;
    setSaveError('');
    let unitPriceMinor: number, taxRateBps: number;
    try {
      unitPriceMinor = paraAltBirime(price);
      taxRateBps = paraAltBirime(tax || '0');
      const nameLength = clean(name).length;
      if (nameLength < 2 || nameLength > 160) throw new Error(T.nameOrPriceInvalid);
      if (!Number.isSafeInteger(unitPriceMinor) || unitPriceMinor < 0 || unitPriceMinor > 2_000_000_000) throw new Error(T.nameOrPriceInvalid);
      if ((clean(description) ? `${clean(name)} · ${clean(description)}` : clean(name)).length > 200) throw new Error(T.tooLong);
      if (clean(supplierName).length > 120) throw new Error(T.supplierTooLong);
      if (!Number.isSafeInteger(taxRateBps) || taxRateBps < 0 || taxRateBps > 10_000) throw new Error(T.taxInvalid);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : T.checkFields); return;
    }
    savingRef.current = true; setSaving(true);
    try {
      const updated = await api.updateSalesProduct(editing.id, { name: clean(name), itemType, description: clean(description) || undefined, supplierName: clean(supplierName) || undefined, currencyCode: editCurrency, unitPriceMinor, taxRateBps });
      setNotice(T.editSaved(updated.name)); setEditing(null); setMode('list'); setRetry(value => value + 1);
      setName(''); setDescription(''); setSupplierName(''); setPrice(''); setTax('0');
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : T.saveFailed);
    } finally { savingRef.current = false; setSaving(false); }
  };
  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (savingRef.current) return;
    setSaveError('');
    let unitPriceMinor: number, taxRateBps: number;
    try {
      unitPriceMinor = paraAltBirime(price);
      taxRateBps = paraAltBirime(tax || '0');
      const nameLength = clean(name).length;
      if (nameLength < 2 || nameLength > 160) throw new Error(T.nameOrPriceInvalid);
      if (!Number.isSafeInteger(unitPriceMinor) || unitPriceMinor < 0 || unitPriceMinor > 2_000_000_000) throw new Error(T.nameOrPriceInvalid);
      if ((clean(description) ? `${clean(name)} · ${clean(description)}` : clean(name)).length > 200) throw new Error(T.tooLong);
      if (clean(supplierName).length > 120) throw new Error(T.supplierTooLong);
      if (!Number.isSafeInteger(taxRateBps) || taxRateBps < 0 || taxRateBps > 10_000) throw new Error(T.taxInvalid);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : T.checkFields); return;
    }
    savingRef.current = true; setSaving(true);
    try {
      const product = await api.createSalesProduct({ name: clean(name), itemType, description: clean(description) || undefined, supplierName: clean(supplierName) || undefined, currencyCode: currency, unitPriceMinor, taxRateBps });
      setName(''); setDescription(''); setSupplierName(''); setPrice(''); setTax('0');
      setRetry(value => value + 1);
      add(product);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : T.saveFailed);
    } finally { savingRef.current = false; setSaving(false); }
  };

  return <div className="saved-product-picker">
    <div className="saved-product-triggers">
      <button type="button" className="ikincil" data-testid="saved-products-open" onClick={() => launch('list')}>{T.openList}</button>
      <button type="button" className="ikincil" data-testid="new-product-open" onClick={() => launch('new')}>{T.openNew}</button>
    </div>
    {notice ? <p role="status" className="saved-product-notice">{notice}</p> : null}
    <dialog ref={dialog} className="saved-product-dialog" aria-labelledby={titleId} onCancel={event => { if (savingRef.current) event.preventDefault(); else setOpen(false); }} onClose={() => setOpen(false)}>
      <header>
        <div><h2 id={titleId}>{mode === 'edit' ? T.titleEdit : mode === 'new' ? T.titleNew : T.titleList}</h2><p>{mode === 'edit' ? T.introEdit : mode === 'new' ? T.introNew : T.introList(currency)}</p></div>
        <button type="button" data-testid="saved-products-close" disabled={saving} onClick={() => setOpen(false)}>{T.close}</button>
      </header>
      <p className="saved-product-scope">{T.scope}</p>
      {mode === 'list' ? <div className="saved-product-list-panel" aria-busy={loading}>
        <div className="saved-product-search-row"><label>{T.search}<input autoComplete="off" data-testid="saved-product-search" value={query} maxLength={160} placeholder={T.searchPlaceholder} onChange={event => { setQuery(event.target.value); setOffset(0); }} /></label><button type="button" onClick={() => { setMode('new'); setSaveError(''); }}>{T.openNew}</button></div>
        {loading ? <p role="status">{T.loading}</p> : null}
        {listError ? <div className="saved-product-error" role="alert"><p>{listError}</p><button type="button" onClick={() => setRetry(value => value + 1)}>{T.retry}</button></div> : null}
        {!loading && !listError && products.length === 0 ? <div className="saved-product-empty"><strong>{query.trim() ? T.noMatch : T.noneInCurrency}</strong><p>{T.emptyHint}</p></div> : null}
        {!loading && !listError && products.length > 0 ? <ul className="saved-product-list">{products.map(product => <li key={product.id}>
          <div><strong>{product.name}</strong><span>{kalemTuru(product.itemType)}{product.supplierName ? ` · ${product.supplierName}` : ''}{product.createdByName ? ` · ${T.createdBy(product.createdByName)}` : ''}</span>{product.description ? <p>{product.description}</p> : null}</div>
          <div className="saved-product-price"><strong>{amountText(product.unitPriceMinor, product.currencyCode)}</strong><small>{T.tax(new Intl.NumberFormat('tr-TR').format((product.taxRateBps ?? 0) / 100))}</small>{usdLine(product) ? <small className="saved-product-usd" data-testid={`saved-product-usd-${product.id}`}>{usdLine(product)}</small> : null}{product.currencyCode !== currency && product.id !== 'ozel-paket' ? <small className="saved-product-currency" data-testid={`saved-product-currency-${product.id}`}>{T.otherCurrency(product.currencyCode, currency)}</small> : null}</div>
          <div className="saved-product-actions">
            <button type="button" data-testid={`saved-product-add-${product.id}`} aria-label={T.addLabel(product.name)} onClick={() => add(product)}>{T.add}</button>
            {product.canEdit ? <button type="button" className="ikincil" data-testid={`saved-product-edit-${product.id}`} aria-label={T.editLabel(product.name)} onClick={() => startEdit(product)}>{T.edit}</button> : null}
          </div>
        </li>)}</ul> : null}
        {!listError && !loading && total > 0 ? <nav className="saved-product-pagination" aria-label={T.pagesLabel}><span>{T.pageRange(offset + 1, Math.min(offset + products.length, total), total)}</span><div><button type="button" disabled={offset === 0} onClick={() => setOffset(value => Math.max(0, value - PAGE_SIZE))}>{T.previous}</button><button type="button" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(value => value + PAGE_SIZE)}>{T.next}</button></div></nav> : null}
      </div> : <form onSubmit={event => void (mode === 'edit' ? saveEdit(event) : save(event))} aria-busy={saving} data-testid={mode === 'edit' ? 'edit-product-form' : undefined}>
        {saveError ? <p role="alert" className="saved-product-error">{saveError}</p> : null}
        <fieldset disabled={saving} className="saved-product-fields"><legend>{T.fields}</legend>
          <label className="saved-product-wide">{T.name} <span aria-hidden="true">*</span><input data-testid="new-product-name" autoComplete="off" required maxLength={160} value={name} onChange={event => setName(event.target.value)} /></label>
          <label>{T.kind}<select data-testid="new-product-kind" value={itemType} onChange={event => setItemType(event.target.value)}>{ITEM_TYPES.map(type => <option key={type} value={type}>{kalemTuru(type)}</option>)}</select></label>
          <label>{T.supplier} <small>{T.optional}</small><input data-testid="new-product-supplier" maxLength={120} value={supplierName} onChange={event => setSupplierName(event.target.value)} /></label>
          {mode === 'edit' && editing ? <label>{T.currency}<select data-testid="edit-product-currency" value={editCurrency} onChange={event => { const next = event.target.value; setEditCurrency(next); setPrice(next === editing.currencyCode ? altBirimText(editing.unitPriceMinor) : ''); }}>{[...new Set([editing.currencyCode, ...CURRENCIES])].map(code => <option key={code} value={code}>{code}</option>)}</select></label> : null}
          {mode === 'edit' && editing && editCurrency !== editing.currencyCode ? <p role="status" className="saved-product-wide saved-product-warning" data-testid="edit-product-currency-warning">{T.currencyChanged(editing.currencyCode, editCurrency)}</p> : null}
          <label>{T.unitPrice(mode === 'edit' ? editCurrency : currency)} <span aria-hidden="true">*</span><input data-testid="new-product-price" required inputMode="decimal" placeholder={T.pricePlaceholder} value={price} onChange={event => setPrice(event.target.value)} />{mode === 'edit' ? null : <small>{T.currencyHint(currency)}</small>}</label>
          <label>{T.taxRate}<input data-testid="new-product-tax" inputMode="decimal" value={tax} onChange={event => setTax(event.target.value)} placeholder={T.taxPlaceholder} /><small>{T.taxHint}</small></label>
          <label className="saved-product-wide">{T.description} <small>{T.optional}</small><textarea data-testid="new-product-description" maxLength={200} value={description} onChange={event => setDescription(event.target.value)} /><small>{T.descriptionHint}</small></label>
        </fieldset>
        <footer><button type="button" disabled={saving} onClick={() => { setMode('list'); setEditing(null); }}>{T.backToList}</button><button type="submit" className="birincil" data-testid={mode === 'edit' ? 'edit-product-save' : 'new-product-save'} disabled={saving}>{saving ? T.saving : mode === 'edit' ? T.saveEdit : T.save}</button></footer>
      </form>}
    </dialog>
  </div>;
}
